/* eslint-disable react/prop-types */
import { ReactElement, ReactNode, WeakValidationMap, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { PortalContextAndRefProps, withPortalDataAndRef } from '../PortalProvider/PortalContext';
import PropTypes, { Validator } from 'prop-types';

export type PortalProps = {
  /** The portal contents */
  children: ReactNode;
} & PortalContextAndRefProps<HTMLDivElement>;

/** Portal
 *
 * Creates a portal to a div within document.body, compatible with the Shadow DOM
 */
const Portal = ({ portalData: { portalNode }, children, forwardedRef }: PortalProps): ReactElement => {
  useImperativeHandle(forwardedRef, () => portalNode, [portalNode]);
  /** Fragment is necessary for react-docgen to recognize this as a functional component */
  return <>{ReactDOM.createPortal(children, portalNode)}</>;
};
Portal.displayName = 'Portal';

const portalPropTypes: WeakValidationMap<PortalProps> = {
  /** The portal contents */
  children: PropTypes.node.isRequired,
  /** The DOM node where the portal contents are mounted */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    /** Note: { current: PropTypes.instanceOf(HTMLDivElement) } would be more
     *  correct, but causes a StackOverflow when building gatsby. */
    PropTypes.shape({ current: PropTypes.any }).isRequired,
  ]) as Validator<React.Ref<HTMLDivElement>>,
};
Portal.propTypes = portalPropTypes;

export default withPortalDataAndRef(Portal);
